import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    SignOut,
    ChartLine,
    Eye,
    Users,
    Clock,
    DeviceMobile,
    DeviceTablet,
    Desktop,
    Globe,
    Funnel,
    Path,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import SEO from "../components/SEO";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

const RANGES = [
    { days: 7, label: "7 jours" },
    { days: 30, label: "30 jours" },
    { days: 90, label: "90 jours" },
];

export default function AdminAnalytics() {
    const { user, loading, logout, api, setUser } = useAuth();
    const navigate = useNavigate();
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        if (loading || !user) return;
        let cancelled = false;
        setLoadingData(true);
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/analytics/traffic?days=${days}`,
                );
                if (!cancelled) setData(data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    setUser(null);
                    navigate("/admin");
                    return;
                }
                toast.error(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                        "Impossible de charger les statistiques.",
                );
            } finally {
                if (!cancelled) setLoadingData(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loading, user, api, navigate, setUser, days]);

    const onLogout = async () => {
        await logout();
        navigate("/admin");
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-scd text-cream flex items-center justify-center">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin" replace />;

    return (
        <main
            data-testid="admin-analytics-page"
            className="min-h-screen bg-scd text-cream"
        >
            <SEO title="Analytics (admin)" path="/admin/analytics" noindex={true} />
            <header className="border-b border-scd bg-[#faf7f1]/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
                    <a href="/" className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Studio Cent Douze"
                            className="h-8 w-auto"
                        />
                        <span className="hidden sm:inline label-eyebrow text-yellow">
                            Admin · Analytics
                        </span>
                    </a>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/briefs"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                            data-testid="admin-briefs-link"
                        >
                            Briefs →
                        </Link>
                        <Link
                            to="/admin/showreel"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                        >
                            Nos réalisations →
                        </Link>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow text-sm transition-colors"
                        >
                            <SignOut size={16} weight="bold" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <section className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                {/* Title + range switcher */}
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                    <div>
                        <span className="label-eyebrow text-yellow">— Trafic</span>
                        <h1 className="font-display text-3xl md:text-4xl mt-1">
                            Statistiques du site
                        </h1>
                        <p className="text-cream/60 text-sm mt-2 max-w-xl">
                            Mesure anonyme du trafic — pageviews, sessions,
                            appareils, régions et entonnoir de conversion.
                            Données 100 % à toi.
                        </p>
                    </div>
                    <RangeSwitcher days={days} onChange={setDays} />
                </div>

                {loadingData || !data ? (
                    <div className="text-center py-20 text-cream/60 label-eyebrow">
                        Chargement des données…
                    </div>
                ) : (
                    <Dashboard data={data} />
                )}
            </section>
        </main>
    );
}

// ------------------------------------------------------------
//                     Dashboard body
// ------------------------------------------------------------
function Dashboard({ data }) {
    const { totals, avg_session_ms, series_daily, hourly_heatmap, top_pages, devices, regions, funnel } = data;

    return (
        <div className="space-y-10">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI
                    icon={<Eye size={16} weight="duotone" className="text-yellow" />}
                    label="Pages vues"
                    value={fmtNum(totals.pageviews)}
                    testId="kpi-pageviews"
                />
                <KPI
                    icon={<Users size={16} weight="duotone" className="text-yellow" />}
                    label="Visiteurs uniques"
                    value={fmtNum(totals.visitors)}
                    testId="kpi-visitors"
                />
                <KPI
                    icon={<Users size={16} weight="duotone" className="text-yellow" />}
                    label="Sessions"
                    value={fmtNum(totals.sessions)}
                    testId="kpi-sessions"
                />
                <KPI
                    icon={<Clock size={16} weight="duotone" className="text-yellow" />}
                    label="Temps moyen / session"
                    value={fmtDuration(avg_session_ms)}
                    testId="kpi-avg-time"
                />
            </div>

            {/* Daily line chart */}
            <Card title="Sessions par jour" testId="chart-daily">
                <DailyChart series={series_daily} />
            </Card>

            {/* Hourly heatmap */}
            <Card
                title="Quand ton audience est active"
                subtitle="Pages vues par jour de la semaine × heure (heure UTC)"
                testId="chart-hourly"
            >
                <HourlyHeatmap heatmap={hourly_heatmap} />
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Funnel */}
                <Card title="Entonnoir de conversion" testId="funnel">
                    <FunnelBlock funnel={funnel} />
                </Card>

                {/* Devices */}
                <Card title="Appareils" testId="devices">
                    <DevicesBlock devices={devices} />
                </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Top pages */}
                <Card title="Pages les plus vues" testId="top-pages">
                    <TopPages pages={top_pages} />
                </Card>

                {/* Regions */}
                <Card title="Régions" testId="regions">
                    <RegionsBlock regions={regions} />
                </Card>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
//                       Sub-components
// ------------------------------------------------------------
function KPI({ icon, label, value, testId }) {
    return (
        <div
            data-testid={testId}
            className="border border-scd bg-elev rounded-sm p-5"
        >
            <div className="flex items-center gap-2 text-cream/60">
                {icon}
                <span className="label-eyebrow">{label}</span>
            </div>
            <div className="mt-2 font-display text-3xl md:text-4xl text-cream">
                {value}
            </div>
        </div>
    );
}

function Card({ title, subtitle, children, testId }) {
    return (
        <section
            data-testid={testId}
            className="border border-scd bg-elev rounded-sm p-5 md:p-6"
        >
            <header className="mb-5">
                <h3 className="font-display text-xl text-cream">{title}</h3>
                {subtitle && (
                    <p className="text-xs text-cream/50 mt-1">{subtitle}</p>
                )}
            </header>
            {children}
        </section>
    );
}

function RangeSwitcher({ days, onChange }) {
    return (
        <div
            className="inline-flex items-center gap-1 p-1 rounded-full border border-scd bg-elev"
            data-testid="range-switcher"
        >
            {RANGES.map((r) => (
                <button
                    key={r.days}
                    onClick={() => onChange(r.days)}
                    data-testid={`range-${r.days}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        days === r.days
                            ? "bg-yellow text-[#272727]"
                            : "text-cream/70 hover:text-cream"
                    }`}
                >
                    {r.label}
                </button>
            ))}
        </div>
    );
}

function DailyChart({ series }) {
    const maxVal = Math.max(...series.map((d) => d.sessions), 1);
    const H = 180;
    const barW = 100 / series.length;

    // Sparse labels: every ~5 days
    const labelEvery = Math.max(1, Math.floor(series.length / 6));

    return (
        <div className="relative">
            <div
                className="relative w-full flex items-end gap-[1px]"
                style={{ height: H }}
            >
                {series.map((d, i) => {
                    const pct = (d.sessions / maxVal) * 100;
                    return (
                        <div
                            key={d.date}
                            className="relative flex-1 group"
                            style={{ minWidth: 4 }}
                            title={`${d.date} · ${d.sessions} session${
                                d.sessions > 1 ? "s" : ""
                            } · ${d.pageviews} vues`}
                        >
                            <div
                                className="w-full rounded-t-sm transition-colors"
                                style={{
                                    height: `${pct}%`,
                                    minHeight: d.sessions > 0 ? 2 : 0,
                                    background:
                                        "linear-gradient(180deg,#ffd140,#3b5838)",
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-cream/50 font-mono">
                {series
                    .filter((_, i) => i % labelEvery === 0 || i === series.length - 1)
                    .map((d) => (
                        <span key={d.date}>{d.date.slice(5)}</span>
                    ))}
            </div>
            <div className="mt-3 text-xs text-cream/55 flex gap-4">
                <span>
                    Max :{" "}
                    <span className="text-cream">{maxVal} sessions</span>
                </span>
                <span>
                    Total sur période :{" "}
                    <span className="text-cream">
                        {series.reduce((s, d) => s + d.sessions, 0)}
                    </span>
                </span>
            </div>
        </div>
    );
}

function HourlyHeatmap({ heatmap }) {
    // Build a 7x24 grid: rows = weekday (1..7 ISO), cols = hour 0..23
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxVal = 0;
    for (const cell of heatmap) {
        if (
            cell.weekday >= 1 &&
            cell.weekday <= 7 &&
            cell.hour >= 0 &&
            cell.hour <= 23
        ) {
            grid[cell.weekday - 1][cell.hour] = cell.count;
            if (cell.count > maxVal) maxVal = cell.count;
        }
    }
    if (maxVal === 0) {
        return (
            <p className="text-cream/50 text-sm">
                Pas encore de données. Reviens dans quelques jours après avoir
                accepté les cookies sur ton site.
            </p>
        );
    }

    const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
    return (
        <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
                <div className="grid" style={{ gridTemplateColumns: "36px repeat(24, minmax(18px, 1fr))" }}>
                    <span />
                    {Array.from({ length: 24 }, (_, h) => (
                        <span
                            key={h}
                            className="text-[9px] text-cream/40 text-center"
                        >
                            {h}
                        </span>
                    ))}
                    {grid.map((row, di) => (
                        <React.Fragment key={di}>
                            <span className="text-[10px] text-cream/50 py-1 pr-2">
                                {days[di]}
                            </span>
                            {row.map((v, hi) => {
                                const intensity = v / maxVal;
                                return (
                                    <div
                                        key={hi}
                                        className="aspect-square rounded-sm border border-scd/40"
                                        style={{
                                            background:
                                                v === 0
                                                    ? "rgba(39,39,39,0.06)"
                                                    : `rgba(255,209,64,${
                                                          0.15 + intensity * 0.85
                                                      })`,
                                        }}
                                        title={`${days[di]} ${hi}h · ${v} vues`}
                                    />
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[10px] text-cream/45">
                    Faible
                    <div className="flex gap-1">
                        {[0.1, 0.3, 0.5, 0.7, 0.95].map((v) => (
                            <div
                                key={v}
                                className="w-4 h-3 rounded-sm"
                                style={{ background: `rgba(255,209,64,${v})` }}
                            />
                        ))}
                    </div>
                    Élevé
                </div>
            </div>
        </div>
    );
}

function FunnelBlock({ funnel }) {
    const steps = [
        {
            label: "Visites (sessions)",
            value: funnel.visits,
            icon: <Eye size={14} weight="duotone" className="text-yellow" />,
        },
        {
            label: "Ont commencé un brief",
            value: funnel.brief_start_sessions,
            icon: <Path size={14} weight="duotone" className="text-yellow" />,
        },
        {
            label: "Ont soumis le brief",
            value: funnel.brief_submit_sessions,
            icon: <Funnel size={14} weight="duotone" className="text-yellow" />,
        },
    ];
    const max = Math.max(...steps.map((s) => s.value), 1);
    const convStart = funnel.visits > 0 ? (funnel.brief_start_sessions / funnel.visits) * 100 : 0;
    const convSubmit = funnel.brief_start_sessions > 0
        ? (funnel.brief_submit_sessions / funnel.brief_start_sessions) * 100
        : 0;

    return (
        <div className="space-y-3">
            {steps.map((s, i) => {
                const pct = (s.value / max) * 100;
                return (
                    <div key={s.label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                            <span className="flex items-center gap-2 text-cream/85">
                                {s.icon}
                                {s.label}
                            </span>
                            <span className="text-cream font-display">
                                {s.value}
                            </span>
                        </div>
                        <div className="h-2 rounded-full bg-scd overflow-hidden">
                            <div
                                className="h-full rounded-full"
                                style={{
                                    width: `${pct}%`,
                                    background:
                                        "linear-gradient(90deg,#ffd140,#3b5838)",
                                }}
                            />
                        </div>
                    </div>
                );
            })}
            <div className="pt-3 mt-1 border-t border-scd flex flex-wrap gap-4 text-xs">
                <span className="text-cream/60">
                    Visite → début brief :{" "}
                    <span className="text-yellow font-medium">
                        {convStart.toFixed(1)} %
                    </span>
                </span>
                <span className="text-cream/60">
                    Début → soumission :{" "}
                    <span className="text-yellow font-medium">
                        {convSubmit.toFixed(1)} %
                    </span>
                </span>
            </div>
        </div>
    );
}

function DevicesBlock({ devices }) {
    if (!devices.length) return <Empty />;
    const total = devices.reduce((s, d) => s + d.sessions, 0);
    const iconFor = (d) => {
        if (d === "mobile")
            return <DeviceMobile size={16} weight="duotone" className="text-yellow" />;
        if (d === "tablet")
            return <DeviceTablet size={16} weight="duotone" className="text-yellow" />;
        return <Desktop size={16} weight="duotone" className="text-yellow" />;
    };
    return (
        <div className="space-y-3">
            {devices.map((d) => {
                const pct = (d.sessions / total) * 100;
                return (
                    <div key={d.device}>
                        <div className="flex items-center justify-between text-sm mb-1">
                            <span className="flex items-center gap-2 text-cream/85 capitalize">
                                {iconFor(d.device)}
                                {d.device}
                            </span>
                            <span className="text-cream">
                                {d.sessions} · {pct.toFixed(0)} %
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-scd overflow-hidden">
                            <div
                                className="h-full rounded-full bg-yellow"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function TopPages({ pages }) {
    if (!pages.length) return <Empty />;
    const max = Math.max(...pages.map((p) => p.views), 1);
    return (
        <ul className="space-y-2">
            {pages.map((p) => {
                const pct = (p.views / max) * 100;
                return (
                    <li key={p.path} className="relative py-1.5 pl-3">
                        <div
                            className="absolute inset-y-0 left-0 rounded-sm pointer-events-none"
                            style={{
                                width: `${pct}%`,
                                background:
                                    "linear-gradient(90deg,rgba(255,209,64,0.18),transparent)",
                            }}
                        />
                        <div className="relative flex justify-between gap-3 text-sm">
                            <span
                                className="text-cream truncate font-mono text-xs"
                                title={p.path}
                            >
                                {p.path || "/"}
                            </span>
                            <span className="text-cream/85 whitespace-nowrap">
                                {p.views}{" "}
                                <span className="text-cream/50 text-xs">
                                    · {p.unique_visitors} uniq.
                                </span>
                            </span>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function RegionsBlock({ regions }) {
    if (!regions.length) return <Empty />;
    const total = regions.reduce((s, r) => s + r.sessions, 0);
    return (
        <ul className="space-y-2">
            {regions.map((r) => {
                const pct = (r.sessions / total) * 100;
                return (
                    <li
                        key={`${r.country}-${r.label}`}
                        className="flex items-center gap-3 text-sm"
                    >
                        <Globe
                            size={14}
                            weight="duotone"
                            className="text-yellow shrink-0"
                        />
                        <span className="text-cream flex-1 truncate">
                            {r.label}
                            {r.country && r.country !== "??" && (
                                <span className="text-cream/45 text-xs ml-1.5">
                                    ({r.country})
                                </span>
                            )}
                        </span>
                        <span className="text-cream/85 whitespace-nowrap">
                            {r.sessions}{" "}
                            <span className="text-cream/50 text-xs">
                                · {pct.toFixed(0)} %
                            </span>
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

function Empty() {
    return (
        <p className="text-cream/50 text-sm">Pas encore de données pour cette période.</p>
    );
}

// ------------------------------------------------------------
//                       Formatters
// ------------------------------------------------------------
function fmtNum(n) {
    if (n == null) return "0";
    if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
    return new Intl.NumberFormat("fr-CA").format(n);
}

function fmtDuration(ms) {
    if (!ms || ms < 1000) return "< 1 s";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s} s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m} min ${rs} s`;
    const h = Math.floor(m / 60);
    return `${h} h ${m % 60} min`;
}
